import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { IconUpload } from '../../components/admin/IconUpload';
import { badgeService } from '../../services/badgeService';
import { Badge, BadgeType, CreateBadgeRequest, UpdateBadgeRequest, AssignBadgeRequest } from '../../types/badge';
import { Modal } from '../../components/ui/Modal';
import { cn } from '../../lib/utils';

// Helper function for auto-assign explanations
const getAutoAssignExplanation = (type: BadgeType): string => {
  const explanations: Record<BadgeType, string> = {
    [BadgeType.ACTIVE_MEMBER]: 'Tự động gán khi user đạt chuỗi đăng nhập ≥ 7 ngày',
    [BadgeType.SUPER_ACTIVE]: 'Tự động gán khi user đạt chuỗi đăng nhập ≥ 30 ngày',
    [BadgeType.CONTRIBUTOR]: 'Tự động gán khi user đăng ≥ 50 bài viết',
    [BadgeType.MASTER_CONTRIBUTOR]: 'Tự động gán khi user đăng ≥ 500 bài viết',
    [BadgeType.COMMENTATOR]: 'Tự động gán khi user có ≥ 200 bình luận',
    [BadgeType.TOP_ENGAGER]: 'Tự động gán khi user có ≥ 1000 lượt tương tác (reactions)',
    [BadgeType.POSITIVE_VIBES]: 'Tự động gán khi user có ≥ 30 bài viết tích cực',
    [BadgeType.INSPIRING_AUTHOR]: 'Tự động gán khi điểm cảm xúc trung bình > 0.8',
    [BadgeType.FRIENDLY]: 'Tự động gán khi user gửi ≥ 20 lời mời kết bạn',
    [BadgeType.WELL_CONNECTED]: 'Tự động gán khi user có ≥ 50 bạn bè',
    [BadgeType.COMMUNITY_BUILDER]: 'Tự động gán khi user có ≥ 100 bạn bè',
    [BadgeType.NEWBIE]: 'Tự động gán cho user mới tạo tài khoản < 7 ngày',
    [BadgeType.ONE_MONTH_MEMBER]: 'Tự động gán khi user tham gia ≥ 30 ngày',
    [BadgeType.THREE_MONTH_LOYAL]: 'Tự động gán khi user tham gia ≥ 90 ngày',
    [BadgeType.ONE_YEAR_VETERAN]: 'Tự động gán khi user tham gia ≥ 365 ngày',
    [BadgeType.VERIFIED]: 'Badge đặc biệt - nên gán thủ công sau xác minh',
    [BadgeType.TRENDSETTER]: 'Badge đặc biệt - nên gán thủ công theo tiêu chí',
    [BadgeType.STAR_OF_THE_WEEK]: 'Badge đặc biệt - nên gán thủ công hàng tuần',
    [BadgeType.PEACEKEEPER]: 'Badge đặc biệt - nên gán thủ công',
    [BadgeType.BUG_HUNTER]: 'Badge đặc biệt - nên gán thủ công cho người báo lỗi',
  };
  return explanations[type] || 'Bật tự động gán nếu badge dựa trên số liệu đo được (bài viết, bạn bè, thâm niên...)';
};

export const AdminBadgesPage: React.FC = () => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [badgeToDelete, setBadgeToDelete] = useState<Badge | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await badgeService.getAllBadges();
      setBadges(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể tải danh sách badge');
      console.error('Failed to load badges:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: CreateBadgeRequest) => {
    try {
      setError(null);
      await badgeService.createBadge(data);
      setShowCreateModal(false);
      await loadBadges();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể tạo badge');
    }
  };

  const handleUpdate = async (badgeId: number, data: UpdateBadgeRequest) => {
    try {
      setError(null);
      await badgeService.updateBadge(badgeId, data);
      setShowEditModal(false);
      setSelectedBadge(null);
      await loadBadges();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể cập nhật badge');
    }
  };

  const handleDelete = async () => {
    if (!badgeToDelete) return;
    try {
      setError(null);
      await badgeService.deleteBadge(badgeToDelete.id);
      setShowDeleteConfirm(false);
      setBadgeToDelete(null);
      await loadBadges();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể xóa badge');
    }
  };

  const handleAssign = async (data: AssignBadgeRequest) => {
    try {
      setError(null);
      await badgeService.assignBadgeToUser(data);
      setShowAssignModal(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Không thể gán badge cho user');
    }
  };

  const getBadgeTypeLabel = (type: BadgeType): string => {
    const labels: Record<BadgeType, string> = {
      [BadgeType.ACTIVE_MEMBER]: 'Thành viên tích cực',
      [BadgeType.SUPER_ACTIVE]: 'Siêu tích cực',
      [BadgeType.CONTRIBUTOR]: 'Người đóng góp',
      [BadgeType.MASTER_CONTRIBUTOR]: 'Bậc thầy đóng góp',
      [BadgeType.COMMENTATOR]: 'Bình luận viên',
      [BadgeType.TOP_ENGAGER]: 'Tương tác hàng đầu',
      [BadgeType.POSITIVE_VIBES]: 'Năng lượng tích cực',
      [BadgeType.INSPIRING_AUTHOR]: 'Tác giả truyền cảm hứng',
      [BadgeType.FRIENDLY]: 'Thân thiện',
      [BadgeType.WELL_CONNECTED]: 'Kết nối tốt',
      [BadgeType.COMMUNITY_BUILDER]: 'Xây dựng cộng đồng',
      [BadgeType.NEWBIE]: 'Thành viên mới',
      [BadgeType.ONE_MONTH_MEMBER]: 'Thành viên 1 tháng',
      [BadgeType.THREE_MONTH_LOYAL]: 'Trung thành 3 tháng',
      [BadgeType.ONE_YEAR_VETERAN]: 'Cựu binh 1 năm',
      [BadgeType.VERIFIED]: 'Đã xác thực',
      [BadgeType.TRENDSETTER]: 'Người dẫn đầu xu hướng',
      [BadgeType.STAR_OF_THE_WEEK]: 'Ngôi sao tuần',
      [BadgeType.PEACEKEEPER]: 'Người giữ hòa bình',
      [BadgeType.BUG_HUNTER]: 'Thợ săn lỗi'
    };
    return labels[type] || type;
  };

  const getBadgeTypeColor = (type: BadgeType): string => {
    const colors: Record<BadgeType, string> = {
      [BadgeType.ACTIVE_MEMBER]: 'bg-blue-100 text-blue-800',
      [BadgeType.SUPER_ACTIVE]: 'bg-purple-100 text-purple-800',
      [BadgeType.CONTRIBUTOR]: 'bg-green-100 text-green-800',
      [BadgeType.MASTER_CONTRIBUTOR]: 'bg-yellow-100 text-yellow-800',
      [BadgeType.COMMENTATOR]: 'bg-indigo-100 text-indigo-800',
      [BadgeType.TOP_ENGAGER]: 'bg-pink-100 text-pink-800',
      [BadgeType.POSITIVE_VIBES]: 'bg-emerald-100 text-emerald-800',
      [BadgeType.INSPIRING_AUTHOR]: 'bg-rose-100 text-rose-800',
      [BadgeType.FRIENDLY]: 'bg-cyan-100 text-cyan-800',
      [BadgeType.WELL_CONNECTED]: 'bg-teal-100 text-teal-800',
      [BadgeType.COMMUNITY_BUILDER]: 'bg-orange-100 text-orange-800',
      [BadgeType.NEWBIE]: 'bg-gray-100 text-gray-800',
      [BadgeType.ONE_MONTH_MEMBER]: 'bg-slate-100 text-slate-800',
      [BadgeType.THREE_MONTH_LOYAL]: 'bg-amber-100 text-amber-800',
      [BadgeType.ONE_YEAR_VETERAN]: 'bg-red-100 text-red-800',
      [BadgeType.VERIFIED]: 'bg-blue-100 text-blue-800',
      [BadgeType.TRENDSETTER]: 'bg-violet-100 text-violet-800',
      [BadgeType.STAR_OF_THE_WEEK]: 'bg-yellow-100 text-yellow-800',
      [BadgeType.PEACEKEEPER]: 'bg-green-100 text-green-800',
      [BadgeType.BUG_HUNTER]: 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <svg className="animate-spin h-12 w-12 text-indigo-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Quản lý Badge</h1>
            <p className="text-gray-600 mt-1">
              Tổng số: {badges.length} badge
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadBadges}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Làm mới
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Tạo Badge
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {badges.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <svg className="w-20 h-20 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            <p className="text-xl text-gray-600 mb-4">Chưa có badge nào</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Tạo badge đầu tiên
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {badges.map((badge) => (
              <div
                key={badge.id}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {badge.iconUrl ? (
                      <img src={badge.iconUrl} alt={badge.name} className="w-12 h-12 rounded-full" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                        {badge.name[0]}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{badge.name}</h3>
                      <span className={cn("inline-flex items-center px-2 py-1 rounded-full text-xs font-medium", getBadgeTypeColor(badge.type))}>
                        {getBadgeTypeLabel(badge.type)}
                      </span>
                    </div>
                  </div>
                </div>

                {badge.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{badge.description}</p>
                )}

                <div className="flex items-center gap-2 mb-4">
                  {badge.isAutoAssignable && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Tự động gán
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedBadge(badge);
                      setShowEditModal(true);
                    }}
                    className="flex-1 px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                  >
                    Sửa
                  </button>
                  <button
                    onClick={() => {
                      setSelectedBadge(badge);
                      setShowAssignModal(true);
                    }}
                    className="flex-1 px-3 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                  >
                    Gán
                  </button>
                  <button
                    onClick={() => {
                      setBadgeToDelete(badge);
                      setShowDeleteConfirm(true);
                    }}
                    className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                  >
                    Xóa
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showCreateModal && (
          <CreateBadgeModal
            isOpen={showCreateModal}
            onClose={() => {
              setShowCreateModal(false);
              setError(null);
            }}
            onSubmit={handleCreate}
          />
        )}

        {showEditModal && selectedBadge && (
          <EditBadgeModal
            isOpen={showEditModal}
            badge={selectedBadge}
            onClose={() => {
              setShowEditModal(false);
              setSelectedBadge(null);
              setError(null);
            }}
            onSubmit={(data) => handleUpdate(selectedBadge.id, data)}
          />
        )}

        {showAssignModal && selectedBadge && (
          <AssignBadgeModal
            isOpen={showAssignModal}
            badge={selectedBadge}
            onClose={() => {
              setShowAssignModal(false);
              setSelectedBadge(null);
              setError(null);
            }}
            onSubmit={handleAssign}
          />
        )}

        {showDeleteConfirm && badgeToDelete && (
          <Modal
            isOpen={showDeleteConfirm}
            onClose={() => {
              setShowDeleteConfirm(false);
              setBadgeToDelete(null);
              setError(null);
            }}
            title="Xác nhận xóa"
          >
            <div className="space-y-4">
              <p className="text-gray-700">
                Bạn có chắc chắn muốn xóa badge <strong>{badgeToDelete.name}</strong>?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setBadgeToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Hủy
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Xóa
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </AdminLayout>
  );
};

interface CreateBadgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBadgeRequest) => void;
}

const CreateBadgeModal: React.FC<CreateBadgeModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState<CreateBadgeRequest>({
    type: BadgeType.ACTIVE_MEMBER,
    name: '',
    description: '',
    iconUrl: '',
    isAutoAssignable: false
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Tạo Badge mới">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Loại Badge <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as BadgeType })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          >
            <option value={BadgeType.ACTIVE_MEMBER}>Thành viên tích cực</option>
            <option value={BadgeType.SUPER_ACTIVE}>Siêu tích cực</option>
            <option value={BadgeType.CONTRIBUTOR}>Người đóng góp</option>
            <option value={BadgeType.MASTER_CONTRIBUTOR}>Bậc thầy đóng góp</option>
            <option value={BadgeType.COMMENTATOR}>Bình luận viên</option>
            <option value={BadgeType.TOP_ENGAGER}>Tương tác hàng đầu</option>
            <option value={BadgeType.POSITIVE_VIBES}>Năng lượng tích cực</option>
            <option value={BadgeType.INSPIRING_AUTHOR}>Tác giả truyền cảm hứng</option>
            <option value={BadgeType.FRIENDLY}>Thân thiện</option>
            <option value={BadgeType.WELL_CONNECTED}>Kết nối tốt</option>
            <option value={BadgeType.COMMUNITY_BUILDER}>Xây dựng cộng đồng</option>
            <option value={BadgeType.NEWBIE}>Thành viên mới</option>
            <option value={BadgeType.ONE_MONTH_MEMBER}>Thành viên 1 tháng</option>
            <option value={BadgeType.THREE_MONTH_LOYAL}>Trung thành 3 tháng</option>
            <option value={BadgeType.ONE_YEAR_VETERAN}>Cựu binh 1 năm</option>
            <option value={BadgeType.VERIFIED}>Đã xác thực</option>
            <option value={BadgeType.TRENDSETTER}>Người dẫn đầu xu hướng</option>
            <option value={BadgeType.STAR_OF_THE_WEEK}>Ngôi sao tuần</option>
            <option value={BadgeType.PEACEKEEPER}>Người giữ hòa bình</option>
            <option value={BadgeType.BUG_HUNTER}>Thợ săn lỗi</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tên Badge <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
            maxLength={100}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mô tả
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            rows={3}
            maxLength={500}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Icon Badge
          </label>
          <IconUpload
            value={formData.iconUrl || ''}
            onChange={(url) => setFormData({ ...formData, iconUrl: url })}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoAssign"
              checked={formData.isAutoAssignable}
              onChange={(e) => setFormData({ ...formData, isAutoAssignable: e.target.checked })}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="autoAssign" className="ml-2 text-sm font-medium text-gray-700">
              Tự động gán badge
            </label>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800 leading-relaxed">
              <span className="font-semibold">💡 Khi nào bật tự động gán?</span><br />
              {getAutoAssignExplanation(formData.type)}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Hủy
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Tạo
          </button>
        </div>
      </form>
    </Modal>
  );
};

interface EditBadgeModalProps {
  isOpen: boolean;
  badge: Badge;
  onClose: () => void;
  onSubmit: (data: UpdateBadgeRequest) => void;
}

const EditBadgeModal: React.FC<EditBadgeModalProps> = ({ isOpen, onClose, onSubmit, badge }) => {
  const [formData, setFormData] = useState<UpdateBadgeRequest>({
    name: badge.name,
    description: badge.description || '',
    iconUrl: badge.iconUrl || '',
    isAutoAssignable: badge.isAutoAssignable
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Chỉnh sửa Badge">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tên Badge
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            maxLength={100}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mô tả
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            rows={3}
            maxLength={500}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Icon Badge
          </label>
          <IconUpload
            value={formData.iconUrl || ''}
            onChange={(url) => setFormData({ ...formData, iconUrl: url })}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="editAutoAssign"
              checked={formData.isAutoAssignable}
              onChange={(e) => setFormData({ ...formData, isAutoAssignable: e.target.checked })}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="editAutoAssign" className="ml-2 text-sm font-medium text-gray-700">
              Tự động gán badge
            </label>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800 leading-relaxed">
              <span className="font-semibold">💡 Khi nào bật tự động gán?</span><br />
              {getAutoAssignExplanation(badge.type)}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Hủy
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Cập nhật
          </button>
        </div>
      </form>
    </Modal>
  );
};

interface AssignBadgeModalProps {
  isOpen: boolean;
  badge: Badge;
  onClose: () => void;
  onSubmit: (data: AssignBadgeRequest) => void;
}

const AssignBadgeModal: React.FC<AssignBadgeModalProps> = ({ isOpen, onClose, onSubmit, badge }) => {
  const [userId, setUserId] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const userIdNum = parseInt(userId, 10);
    if (!isNaN(userIdNum)) {
      onSubmit({ userId: userIdNum, badgeId: badge.id });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Gán Badge: ${badge.name}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            User ID <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
            min="1"
            placeholder="Nhập ID của user"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Hủy
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Gán Badge
          </button>
        </div>
      </form>
    </Modal>
  );
};

